/**
 * Installment distribution algorithms used by payment plan and credit configurators.
 *
 * All functions are pure: they take a total and a count, and return a number[]
 * whose sum equals the total. Installments 1…n-1 are rounded to the nearest step
 * (roundTo: 1, 10, 25, 50, or 100); the last installment absorbs any remainder
 * so the sum is always exact.
 */

export const ROUND_TO_OPTIONS = [0.01, 1, 5, 10, 25, 50, 100] as const
export type RoundToOption = (typeof ROUND_TO_OPTIONS)[number]

/**
 * Suggests a round-to step based on total amount and installment count.
 * Uses average installment size: smaller payments → finer rounding ($1), larger → coarser ($100).
 * Never auto-suggests 0.01 ("None") — that is user-selected only.
 */
export function suggestRoundTo(total: number, count: number): RoundToOption {
  if (count <= 0 || total <= 0) return 10
  const avg = total / count
  if (avg < 25) return 1
  if (avg < 100) return 10
  if (avg < 250) return 25
  if (avg < 1000) return 50
  return 100
}

/**
 * Returns true if a round-to step is useful for the given total/count combination.
 * A step is considered irrelevant when floor-rounding causes the last installment
 * to be more than 2× the base installment (too large a discrepancy).
 * "None" (0.01) is always relevant.
 */
export function isRoundToRelevant(total: number, count: number, roundTo: RoundToOption): boolean {
  if (count <= 1 || roundTo === 0.01) return true
  const base = Math.floor(total / count / roundTo) * roundTo
  if (base <= 0) return false
  const last = total - base * (count - 1)
  return last >= 0 && last <= base * 2
}

export function roundToNearest(value: number, step: number): number {
  if (step <= 0) return value
  return Math.round(value / step) * step
}

function roundToFloor(value: number, step: number): number {
  if (step <= 0) return value
  return roundCents(Math.floor(value / step) * step)
}

function roundCents(value: number): number {
  return Math.round(value * 100) / 100
}

export function equalSplit(total: number, count: number, roundTo: number = 1): number[] {
  if (count <= 0 || total <= 0) return Array(Math.max(0, count)).fill(0)
  if (count === 1) return [roundCents(total)]
  const base = roundToFloor(total / count, roundTo)
  const arr = Array(count).fill(base)
  const sumOfFirst = base * (count - 1)
  arr[count - 1] = roundCents(total - sumOfFirst)
  return arr
}

export function frontLoadedSplit(total: number, count: number, roundTo: number = 1): number[] {
  if (count <= 1 || total <= 0) return equalSplit(total, count, roundTo)
  const firstShare = roundToFloor((total * 2) / (count + 1), roundTo)
  const restShare = roundToFloor((total - firstShare) / (count - 1), roundTo)
  const arr = Array(count).fill(restShare)
  arr[0] = firstShare
  const sum = arr.reduce((s: number, v: number) => s + v, 0)
  arr[count - 1] = roundCents(arr[count - 1] + (total - sum))
  return arr
}

export function graduatedDecreasingSplit(total: number, count: number, roundTo: number = 1): number[] {
  if (count <= 1 || total <= 0) return equalSplit(total, count, roundTo)
  const totalWeight = (count * (count + 1)) / 2
  const arr = Array.from({ length: count }, (_, i) =>
    roundToFloor((total * (count - i)) / totalWeight, roundTo)
  )
  const sum = arr.reduce((s, v) => s + v, 0)
  arr[count - 1] = roundCents(arr[count - 1] + (total - sum))
  return arr
}

export function graduatedIncreasingSplit(total: number, count: number, roundTo: number = 1): number[] {
  if (count <= 1 || total <= 0) return equalSplit(total, count, roundTo)
  const totalWeight = (count * (count + 1)) / 2
  const arr = Array.from({ length: count }, (_, i) =>
    roundToFloor((total * (i + 1)) / totalWeight, roundTo)
  )
  const sum = arr.reduce((s, v) => s + v, 0)
  arr[count - 1] = roundCents(arr[count - 1] + (total - sum))
  return arr
}

/**
 * Redistribute a `remaining` amount across `freeSlots` indices within `installments`.
 * Rounds slots 1…n-1 to nearest roundTo, putting the remainder in the last free slot.
 * Mutates and returns the provided array.
 */
export function redistributeAcrossSlots(
  installments: number[],
  freeSlots: number[],
  remaining: number,
  roundTo: number = 1
): number[] {
  if (freeSlots.length === 0) return installments
  const perFree = roundToFloor(remaining / freeSlots.length, roundTo)
  let distributed = 0
  for (let j = 0; j < freeSlots.length - 1; j++) {
    installments[freeSlots[j]] = perFree
    distributed += perFree
  }
  installments[freeSlots[freeSlots.length - 1]] = roundCents(remaining - distributed)
  return installments
}
